import React, { useState } from 'react';
import { Rnd } from 'react-rnd';
import RotateHandle from './RotateHandle';
import ChairIcon from './ChairIcon';
import { getChairPositions } from '../../utils/chairLayout';
import { MainsReadyIcon, DessertReadyIcon } from './OrderActionIcons';

const MIN_SIZE = 40;

// The size a table gets by default (TablePropertiesModal's circle/square dimensions) - the point
// at which the action-overlay/timer render at their normal, full-comfort size. Below this, staff
// have deliberately shrunk the table (to fit more of them in), so the overlay shrinks with it
// instead of keeping a fixed footprint several times the table's own size - which is what let
// neighbouring tables' overlays collide even when each table's own spacing looked reasonable.
const OVERLAY_BASELINE_SIZE = 90;
const OVERLAY_SCALE_FLOOR = 0.6;

// Every table defaults to orange until staff pick a different color in the editor. Once a table
// is occupied (order view only), it always renders green — overriding whatever color it was
// given — so "is this table free?" reads at a glance regardless of anyone's color choices.
const DEFAULT_TABLE_COLOR = '#ff5722';
const OCCUPIED_TABLE_COLOR = '#1b9d2a';

// Stops the click from also bubbling up to Rnd's own onMouseDown (which selects/deselects the
// table) — without this, tapping an overlay button would both fire its action AND toggle
// selection underneath it.
const stopBubble = (e) => e.stopPropagation();

/**
 * `readOnly` switches this from the floor-plan editor's drag/resize/rotate mode into an
 * operational "pick a table to order" mode (used by FloorPlanDashboardPage): dragging/resizing
 * is disabled, no rotate handle, and the occupancy/action affordances DashboardPage's grid tiles
 * already have (elapsed timer, checkout, mains/dessert kitchen-print) render as an overlay above
 * the table instead — positioned high enough to clear the chair ring regardless of table size.
 */
const TableShape = ({
  table,
  isSelected,
  isSoleSelection = true,
  onSelect,
  onChange,
  readOnly = false,
  hideChairs = false,
  isOccupied = false,
  elapsedLabel = '',
  isMultiSelected = false,
  onCheckout,
  onPrintMains,
  onPrintDessert,
  onInfoClick,
  canvasScale = 1,
  isDraft = false,
}) => {
  const [actionsExpanded, setActionsExpanded] = useState(false);
  const lockAspect = table.shape === 'circle' || table.shape === 'square';
  const overlayScale = Math.max(
    OVERLAY_SCALE_FLOOR,
    Math.min(1, Math.min(table.width, table.height) / OVERLAY_BASELINE_SIZE)
  );

  const handleDragStop = (e, d) => {
    // Nothing constrains position *during* the drag in the editor (see the Rnd `bounds` comment
    // below) - a table dragged up/left past the origin would otherwise land at a permanently
    // negative, unreachable position (off the top/left edge, which the canvas has no direction to
    // scroll toward). Clamping only here, after the drop, still allows the drag itself to be
    // completely free.
    onChange(table.id, { position_x: Math.max(0, Math.round(d.x)), position_y: Math.max(0, Math.round(d.y)) });
  };

  const handleResizeStop = (e, direction, ref, delta, position) => {
    onChange(table.id, {
      width: Math.round(ref.offsetWidth),
      height: Math.round(ref.offsetHeight),
      position_x: Math.round(position.x),
      position_y: Math.round(position.y),
    });
  };

  const handleRotate = (angle) => {
    onChange(table.id, { rotation: angle });
  };

  const chairs = hideChairs ? [] : getChairPositions({
    shape: table.shape,
    width: table.width,
    height: table.height,
    seatCount: table.number_of_people,
  });

  // Occupied (order view only) stays a strong solid fill — that's a functional at-a-glance
  // signal, not a decorative choice, so it keeps full color regardless of the table's own
  // color. A table's own color is decorative identity, so it renders as an outline against a
  // fixed dark fill instead of taking over the whole shape.
  const isOccupiedFill = readOnly && isOccupied;
  const accentColor = isOccupiedFill ? OCCUPIED_TABLE_COLOR : (table.color || DEFAULT_TABLE_COLOR);

  return (
    <Rnd
      // A table saved without an explicit size comes back null from the API - passing that
      // straight through crashes react-rnd/re-resizable internally (it calls .toString() on
      // the raw value with no null guard of its own), taking down the whole dashboard render.
      size={{ width: table.width ?? MIN_SIZE, height: table.height ?? MIN_SIZE }}
      position={{ x: table.position_x, y: table.position_y }}
      lockAspectRatio={lockAspect}
      minWidth={MIN_SIZE}
      minHeight={MIN_SIZE}
      // `bounds="parent"` clamps to a *snapshot* of the parent's size taken when the drag starts -
      // react-rnd resolves "parent" into a plain {top,right,bottom,left} object once, not a live
      // re-measurement, so growing the canvas mid-drag (via onChange -> a bigger saved bounding
      // box) never actually extends an *already-active* drag - only a fresh one, which is what
      // made growing the editor's canvas feel like a multi-step ratchet (drag table A to the edge,
      // drop, drag table B a bit further, drop, repeat). react-rnd's own `bounds` prop only
      // accepts a selector string or an Element (not a partial {left,top} object - passing one
      // silently falls back to an all-zero bounds, clamping every drag to the origin), so there's
      // no equivalent "only constrain this side" option to reach for here either. Only the
      // read-only order-view dashboard (dragging disabled there anyway) keeps the snapshot-bounded
      // "parent" behavior; the editor drops bounds entirely so a single continuous drag can extend
      // the canvas as far as it goes in one motion - it still grows to fit wherever the table gets
      // dropped, and handleDragStop below clamps the *result* to non-negative so nothing can end
      // up off the top/left edge with no way to scroll back to it.
      bounds={readOnly ? 'parent' : undefined}
      scale={canvasScale}
      disableDragging={readOnly}
      enableResizing={!readOnly}
      onDragStop={readOnly ? undefined : handleDragStop}
      onResizeStop={readOnly ? undefined : handleResizeStop}
      style={{ '--overlay-scale': overlayScale }}
      onMouseDown={(e) => {
        // react-draggable calls this unconditionally on mousedown/touchstart, before it even
        // checks its own `handle`/`cancel` selectors - those only gate whether a drag actually
        // starts, not this callback. On a mouse, the action-overlay buttons' own
        // onMouseDown={stopBubble} stops the *synthetic* event from ever reaching here, but
        // react-draggable binds this to the DOM node's native `touchstart` directly (bypassing
        // React's event system), so on a touchscreen it always fires first regardless - which is
        // what let a tap on any of the 4 overlay buttons also select/navigate the table
        // underneath it. Checking the real target here (available on both mouse and touch)
        // catches it on both, independent of that timing.
        if (e.target.closest && e.target.closest('.tableShapeActionsOverlay')) return;
        e.stopPropagation();
        onSelect(table.id, e);
      }}
      className={`tableShape${isSelected || isMultiSelected ? ' selected' : ''}${isOccupied ? ' reserved' : ''}${isDraft ? ' draftTable' : ''}`}
    >
      <div className="tableShapeRotator" style={{ transform: `rotate(${table.rotation || 0}deg)` }}>
        <div
          className={`tableShapeInner tableShape--${table.shape}`}
          style={isOccupiedFill
            ? { background: accentColor, borderColor: accentColor }
            : { borderColor: accentColor }}
        >
          <div className="tableShapeLabel">
            <span className="tableShapeName">
              <svg width="21" height="14" className="tableNumberIcon">
                <use xlinkHref="/images/sprite.svg#iconTableNumber" />
              </svg>
              #{table.name}
            </span>
            <span className="tableShapeSeats">
              <svg width="11" height="10" className="personIcon">
                <use xlinkHref="/images/sprite.svg#iconPersons" />
              </svg>
              {table.number_of_people}
            </span>

            {/* EXPERIMENT: elapsed time sitting on the table itself, below the seat count,
                instead of floating above the table - see how this reads compared to the old
                overlay badge. */}
            {readOnly && isOccupied && (
              <span className="tableShapeTimerInline">{elapsedLabel}</span>
            )}
          </div>
        </div>

        {!hideChairs && (
          <div className="tableShapeChairs">
            {chairs.map((chair, index) => (
              <ChairIcon
                key={index}
                style={{
                  left: chair.x,
                  top: chair.y,
                  transform: `translate(-50%, -50%) rotate(${chair.rotation}deg)`,
                }}
              />
            ))}
          </div>
        )}
      </div>

      {!readOnly && isSelected && isSoleSelection && <RotateHandle onRotate={handleRotate} />}

      {readOnly && isOccupied && (
        <div className={`tableShapeActionsOverlay${actionsExpanded ? ' expanded' : ''}`}>
          <button
            type="button"
            className="tableShapeActionBtn tableShapeActionBtn--toggle"
            title={actionsExpanded ? 'Hide table actions' : 'Table actions'}
            onMouseDown={stopBubble}
            onClick={(e) => {
              stopBubble(e);
              setActionsExpanded((v) => !v);
            }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <circle cx="5" cy="12" r="2.2" fill="currentColor" />
              <circle cx="12" cy="12" r="2.2" fill="currentColor" />
              <circle cx="19" cy="12" r="2.2" fill="currentColor" />
            </svg>
          </button>

          <span className="tableShapeActionWrap">
            <button
              type="button"
              className="tableShapeActionBtn tableShapeActionBtn--info"
              title="View Order Items"
              tabIndex={actionsExpanded ? 0 : -1}
              onMouseDown={stopBubble}
              onClick={(e) => {
                stopBubble(e);
                onInfoClick();
              }}
            >
              <svg width="22" height="22"><use xlinkHref="/images/sprite.svg#iconInfo" /></svg>
            </button>
          </span>
          <span className="tableShapeActionWrap">
            <button
              type="button"
              className="tableShapeActionBtn tableShapeActionBtn--mains"
              title="Ready for Mains"
              tabIndex={actionsExpanded ? 0 : -1}
              onMouseDown={stopBubble}
              onClick={(e) => {
                stopBubble(e);
                onPrintMains();
              }}
            >
              <MainsReadyIcon background="none" width="22" height="22" />
            </button>
          </span>
          <span className="tableShapeActionWrap">
            <button
              type="button"
              className="tableShapeActionBtn tableShapeActionBtn--dessert"
              title="Ready for Desserts"
              tabIndex={actionsExpanded ? 0 : -1}
              onMouseDown={stopBubble}
              onClick={(e) => {
                stopBubble(e);
                onPrintDessert();
              }}
            >
              <DessertReadyIcon background="none" width="22" height="22" />
            </button>
          </span>
          {!isSelected && !isMultiSelected && (
            <span className="tableShapeActionWrap">
              <button
                type="button"
                className="tableShapeActionBtn tableShapeActionBtn--checkout"
                title="Checkout"
                tabIndex={actionsExpanded ? 0 : -1}
                onMouseDown={stopBubble}
                onClick={(e) => {
                  stopBubble(e);
                  onCheckout();
                }}
              >
                <svg width="22" height="22"><use xlinkHref="./images/sprite.svg#iconCheckout" /></svg>
              </button>
            </span>
          )}
        </div>
      )}
    </Rnd>
  );
};

export default TableShape;
